import { isRunningInNativeApp } from '~/utils/browserInfo';

/**
 * Pull-to-refresh para o WebView nativo. So ativa quando o site esta rodando
 * dentro do app (`window.ReactNativeWebView` presente) — usuarios do navegador
 * mantem o pull-to-refresh nativo do Chrome/Safari, sem concorrencia.
 *
 * O scroll observado e o de `document.body` (padrao do projeto — ver
 * `WidgetsBackToTop`). O gesto so dispara quando `body.scrollTop === 0` no
 * inicio do toque, ignorando carrosseis (gesto horizontal) e situacoes
 * em que outra UI ja sequestra o scroll (modal aberto, menu lateral aberto).
 *
 * Retorna refs reativos pro componente do indicador desenhar a animacao.
 */

interface Options {
  /** Distancia de dedo (px) para disparar reload. */
  triggerDistance?: number;
  /** Fator de resistencia: spinner desce `delta * resistance` px. */
  resistance?: number;
  /** Limite visual do spinner em px. */
  maxDisplay?: number;
}

export function usePullToRefresh(options: Options = {}) {
  const { triggerDistance = 120, resistance = 0.5, maxDisplay = 80 } = options;

  const pullDistance = ref(0);
  const isReloading = ref(false);
  // O usuario passou do threshold neste gesto (usado pelo indicador pra
  // mostrar visualmente "solte para atualizar").
  const isTriggered = computed(() => {
    return pullDistance.value >= maxDisplay * 0.75;
  });

  let startY = 0;
  let startX = 0;
  let tracking = false;
  let direction: 'vertical' | 'horizontal' | null = null;

  // Lazy: store so existe client-side. Resolve no onMounted.
  let menuOpenRef: { value: boolean } | null = null;

  const getBodyScrollTop = (): number => {
    return document.body.scrollTop || document.documentElement.scrollTop || 0;
  };

  const isEligible = (): boolean => {
    if (!isRunningInNativeApp()) {
      return false;
    }
    if (isReloading.value) {
      return false;
    }
    if (menuOpenRef?.value) {
      return false;
    }
    if (document.body.classList.contains('modal-open')) {
      return false;
    }
    if (getBodyScrollTop() > 0) {
      return false;
    }

    return true;
  };

  const reset = (): void => {
    tracking = false;
    direction = null;
    pullDistance.value = 0;
  };

  const onTouchStart = (event: TouchEvent): void => {
    const touch = event.touches[0];

    if (event.touches.length !== 1 || !touch) {
      reset();

      return;
    }
    if (!isEligible()) {
      tracking = false;

      return;
    }
    tracking = true;
    direction = null;
    startY = touch.clientY;
    startX = touch.clientX;
    pullDistance.value = 0;
  };

  const onTouchMove = (event: TouchEvent): void => {
    if (!tracking) {
      return;
    }

    const touch = event.touches[0];

    if (!touch) {
      return;
    }

    const deltaY = touch.clientY - startY;
    const deltaX = touch.clientX - startX;

    // Trava de direcao: assim que o gesto se define como horizontal,
    // soltamos o controle pra carrossel/scroll lateral funcionarem.
    if (direction === null) {
      const absY = Math.abs(deltaY);
      const absX = Math.abs(deltaX);

      if (absY < 6 && absX < 6) {
        return;
      }
      direction = absY > absX ? 'vertical' : 'horizontal';
      if (direction === 'horizontal') {
        reset();

        return;
      }
    }

    if (deltaY <= 0) {
      pullDistance.value = 0;

      return;
    }

    if (getBodyScrollTop() > 0) {
      reset();

      return;
    }

    // Bloqueia o overscroll nativo (bounce iOS / chrome refresh) pra que o
    // nosso indicador seja a unica resposta visual ao gesto.
    if (event.cancelable) {
      event.preventDefault();
    }

    pullDistance.value = Math.min(deltaY * resistance, maxDisplay);
  };

  const finishGesture = (): void => {
    if (!tracking) {
      pullDistance.value = 0;

      return;
    }
    tracking = false;

    const fingerTravel = pullDistance.value / resistance;

    if (fingerTravel >= triggerDistance && !isReloading.value) {
      isReloading.value = true;
      pullDistance.value = maxDisplay;
      // Pequeno delay pra o usuario ver o estado "vou recarregar" antes do
      // browser comecar a desmontar a pagina.
      setTimeout(() => {
        // iOS (WKWebView) mantem o documento antigo pintado durante o reload,
        // entao a checagem de render do app confirmava contra a pagina velha e
        // escondia o loading cedo. `display: none` zera innerText e os rects do
        // body: a heuristica ve "nao pintado" e o loading so sai com a pagina
        // nova — replicando o Android, que esvazia o documento na hora.
        document.body.style.display = 'none';
        window.location.reload();
      }, 180);

      return;
    }
    pullDistance.value = 0;
    direction = null;
  };

  onMounted(() => {
    const layoutStore = useLayoutStore();

    menuOpenRef = storeToRefs(layoutStore).menuOpen;

    // touchmove precisa `passive: false` pra poder chamar preventDefault.
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', finishGesture, { passive: true });
    document.addEventListener('touchcancel', finishGesture, { passive: true });
  });

  onUnmounted(() => {
    document.removeEventListener('touchstart', onTouchStart);
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('touchend', finishGesture);
    document.removeEventListener('touchcancel', finishGesture);
  });

  return {
    pullDistance,
    isTriggered,
    isReloading,
    maxDisplay,
  };
}
